import React, { useEffect, useState } from 'react';
import { readSharedOffer, acceptSharedOffer, type SharedOffer } from '@/lib/sharedOffers';
import { useAppContext } from '@/store/AppContext';
import { buildClientOfferItemRows, getClientMaterialModeLabel } from '@/domain/clientOfferPresentation';
import { buildClientPdfSummaryRows } from '@/pdf/buildClientPdfSummaryRows';
import { Button } from '../components/ui/button';
import { Logo } from '../components/Logo';

const money = (value: number) => value.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' zł';

const ClientOffer: React.FC = () => {
  const projectId = window.location.pathname.split('/offer/')[1]?.split('/')[0];
  const [project, setProject] = useState<SharedOffer | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [offerError, setOfferError] = useState('');
  const { user, login } = useAppContext();
  const token = window.location.hash.slice(1);

  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }

      try {
        const response = await readSharedOffer(token);
        setProject(response.offer);
      } catch (error) {
        console.error("Error fetching project for client:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectId, token]);

  const handleAccept = async () => {
    if (accepting || accepted) return;
    setAccepting(true);
    setOfferError('');
    try {
      if (!user) { await login(); return; }
      await acceptSharedOffer(token);
      setAccepted(true);
    } catch (error) {
      setOfferError(error instanceof Error ? error.message : 'Nie udało się zaakceptować oferty.');
    } finally { setAccepting(false); }
  };
  if (loading) return <p role="status" className="p-6">Wczytywanie oferty...</p>;
  if (!project) return (
    <main className="max-w-xl mx-auto p-6 space-y-4">
      <h1 className="text-2xl font-bold">Oferta niedostępna</h1>
      <p>Link wygasł lub oferta wymaga ponownego udostępnienia przez wykonawcę.</p>
      <Button className="h-14" onClick={() => window.location.href = '/'}>Wróć do strony głównej</Button>
    </main>
  );
  const summary = buildClientPdfSummaryRows({
    laborNet: project.workNet, materialsNet: project.materialNet, netTotal: project.netTotal,
    vatLabel: project.vatLabel, vatRate: project.vatPercent, vatValue: project.vatAmount,
    grossTotal: project.grossTotal, materialMode: project.materialMode,
  });
  const scopes = [
    { title: 'W cenie uwzględniono', labels: project.includedScopeLabels, note: 'Prace objęte ceną oferty, bez osobnej pozycji i bez dopłaty.' },
    { title: 'Poza zakresem prac', labels: project.excludedScopeLabels, note: 'Wykonawca oznaczył te prace jako niedotyczące tej wyceny.' },
    { title: 'Nieujęte w cenie — do ustalenia', labels: project.unresolvedScopeLabels, note: 'Te prace nie są ujęte w cenie i wymagają odrębnego potwierdzenia lub wyceny.' },
  ];
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 pb-12">
      <header className="bg-white border-b p-4"><div className="max-w-4xl mx-auto"><Logo variant="dark" /></div></header>
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <section className="bg-slate-900 text-white p-6 rounded-2xl space-y-3">
          <p>Oferta dla klienta</p>
          <h1 className="text-2xl font-bold break-words">{project.name}</h1>
          {project.clientName && <p>Klient: {project.clientName}</p>}
          <p className="text-3xl font-bold">{money(project.grossTotal)}</p>
          <p>{project.settlementLabel}</p>
        </section>
        <section className="space-y-3">
          <h2 className="text-xl font-bold">Pozycje oferty</h2>
          <ul className="space-y-3">{buildClientOfferItemRows(project).map((item, index) => (
            <li key={index} className="bg-white border rounded-xl p-4 space-y-2">
              <p className="font-semibold break-words">{item.label}</p>
              <p>{item.quantity} {item.unit} · {money(item.unitPrice)} / {item.unit}</p>
              <p className="font-bold">{money(item.total)}</p>
            </li>
          ))}</ul>
        </section>
        <section className="bg-white border rounded-xl p-4 space-y-3">
          <h2 className="text-xl font-bold">Cena dla klienta</h2>
          <dl className="space-y-2">{summary.summaryRows.map(row => (
            <div key={row.label} className="flex flex-wrap justify-between gap-2"><dt>{row.label}</dt><dd className="font-bold">{row.value}</dd></div>
          ))}</dl>
          <p>{project.vatLabel}</p>
        </section>
        <section className="bg-white border rounded-xl p-4 space-y-3">
          <h2 className="text-xl font-bold">{getClientMaterialModeLabel(project.materialMode)}</h2>
          {summary.materialNote && <p>{summary.materialNote}</p>}
          {project.materialLines.length > 0 && <ul>{project.materialLines.map((line, index) => (
            <li key={index}>{line.label} — {line.quantityLabel}: {money(line.clientPrice)}</li>
          ))}</ul>}
        </section>
        {scopes.filter(scope => scope.labels.length > 0).map(scope => (
          <section key={scope.title} className="bg-white border rounded-xl p-4 space-y-3">
            <h2 className="text-xl font-bold">{scope.title}</h2>
            <ul className="list-disc pl-5">{scope.labels.map(label => <li key={label}>{label}</li>)}</ul>
            <p>{scope.note}</p>
          </section>
        ))}
        <p>Oferta obejmuje ustalony zakres prac. Prace dodatkowe, zmiana standardu materiałów lub warunki ujawnione po rozpoczęciu realizacji wymagają osobnego potwierdzenia.</p>
        {offerError && <p role="alert" className="text-red-700">{offerError}</p>}
        {accepted ? <p role="status" className="font-bold text-emerald-800">Oferta zaakceptowana.</p> : <>
          {!user && <p>Akceptacja oferty wymaga zalogowania przez Google. Po zalogowaniu ponownie wybierz akceptację.</p>}
          <Button className="w-full h-14 text-lg" onClick={handleAccept} disabled={accepting}>
            {accepting ? 'Proszę czekać...' : 'AKCEPTUJĘ OFERTĘ'}
          </Button>
        </>}
      </main>
    </div>
  );
};

export default ClientOffer;
